import { createContext, useMemo, useContext, useState } from 'react';
import { useStaticContent } from '@dom-digital-online-media/dom-static-content-sdk';
import PropTypes from 'prop-types';
import { stripHtml } from 'string-strip-html';

// A context to load accessibility functionality
const A11yContext = createContext();

// The top level component that will wrap our app's core features
export function A11yProvider({ children }) {
  // Hooks
  const { getStaticContentValue } = useStaticContent();

  // States
  const [isGenericApiError, setIsGenericApiError] = useState(false);

  // Functions
  const tA11yTranslate = (key) => {
    const content = getStaticContentValue(key);

    if (content.content === '') {
      content.content = key;
    }

    return {
      ...content,
      hasAriaLabel: !!content.ariaLabel, // !! converts the value to a boolean
      hasAriaDescription: !!content.ariaDescription,
      isHtml: content.type === 'html' // checks if the content type is 'html'
    };
  };

  // META: global page titles
  const getGlobalMetaTitles = () => ({
    globalPageTitle: tA11yTranslate('ek_global_meta_title').content,
    globalSuccessPageTitle: tA11yTranslate('ek_global_meta_title_success').content,
    globalErrorPageTitle: tA11yTranslate('ek_global_meta_title_error').content
  });

  const getStripeHTMLText = (input) => {
    const { result } = stripHtml(input);
    return result;
  }

  // We wrap it in a useMemo for performance reasons.
  const contextPayload = useMemo(
    () => ({
      // States
      isGenericApiError,
      setIsGenericApiError,

      // Functions
      tA11yTranslate,
      getGlobalMetaTitles,
      getStripeHTMLText
    }),
    [
      // States
      isGenericApiError,
      setIsGenericApiError,

      // Functions
      tA11yTranslate,
      getGlobalMetaTitles,
      getStripeHTMLText
    ]
  );

  // We expose the context's value down to our components, while
  // also making sure to render the proper content to the screen
  return <A11yContext.Provider value={contextPayload}>{children}</A11yContext.Provider>;
}

A11yProvider.propTypes = {
  children: PropTypes.node
};

// A custom hook to quickly read the context's value. It's
// only here to allow quick imports
export const useA11y = () => useContext(A11yContext);

export default A11yContext;
